import FadeUp from "./FadeUp";
import { Check, X, ArrowRight } from "lucide-react";

const rows = [
    {
        label: "Who you actually work with",
        agency: "Rotating juniors across many clients",
        mine: "Me on strategy. One dedicated AM on execution.",
    },
    {
        label: "What you own at the end",
        agency: "Nothing. Relationships and data leave with them.",
        mine: "Creators, SOPs, tooling, affiliate database, tracking.",
    },
    {
        label: "Timeline",
        agency: "Indefinite monthly retainer",
        mine: "Three months to a working, documented system.",
    },
    {
        label: "Fit",
        agency: "Any brand with a budget",
        mine: "Productivity apps only.",
    },
    {
        label: "After the engagement",
        agency: "You stay on retainer or lose the channel",
        mine: "Your team runs it. Ongoing support is optional.",
    },
    {
        label: "Reporting",
        agency: "Vanity dashboards on their timeline",
        mine: "Weekly reviews with the numbers that matter.",
    },
];

const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const WhyDifferent = () => (
    <section
        id="why-different"
        data-testid="why-different-section"
        className="py-24 md:py-32 bg-[#FAFAFA] border-y border-black/[0.06]"
    >
        <div className="container-page">
            <div className="max-w-3xl">
                <FadeUp>
                    <p className="text-xs font-bold tracking-[0.22em] uppercase text-gray-500">
                        Why this is different
                    </p>
                </FadeUp>
                <FadeUp delay={0.05}>
                    <h2
                        data-testid="why-different-heading"
                        className="mt-4 font-display font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.3] text-[#0A0A0A] text-balance"
                    >
                        An operator, not an agency.
                    </h2>
                </FadeUp>
                <FadeUp delay={0.1}>
                    <p className="mt-5 text-[15.5px] md:text-base text-gray-600 leading-relaxed max-w-2xl">
                        A calm side-by-side on what you actually get, and what you actually keep.
                    </p>
                </FadeUp>
            </div>

            <FadeUp delay={0.15}>
                <div className="mt-12 overflow-x-auto">
                    <div
                        data-testid="comparison-table"
                        className="min-w-[720px] rounded-2xl border border-black/[0.08] bg-white overflow-hidden"
                    >
                        <div className="grid grid-cols-[1.15fr_1fr_1fr] bg-[#FAFAFA] border-b border-black/[0.08]">
                            <div className="p-4 md:p-5" />
                            <div className="p-4 md:p-5 text-[12px] font-bold tracking-[0.15em] text-gray-500 uppercase">
                                Typical agency
                            </div>
                            <div className="p-4 md:p-5 text-[12px] font-bold tracking-[0.15em] text-[#0A0A0A] uppercase bg-white border-l border-black/[0.08]">
                                My approach
                            </div>
                        </div>
                        {rows.map((row, i) => (
                            <div
                                key={row.label}
                                data-testid={`comparison-row-${i}`}
                                className={`grid grid-cols-[1.15fr_1fr_1fr] ${
                                    i < rows.length - 1 ? "border-b border-black/[0.08]" : ""
                                }`}
                            >
                                <div className="p-4 md:p-5 font-display font-semibold text-[14.5px] md:text-[15px] text-[#0A0A0A] tracking-tight">
                                    {row.label}
                                </div>
                                <div className="p-4 md:p-5 flex items-start gap-2.5 text-[13.5px] md:text-[14px] leading-[1.65] text-gray-500">
                                    <X
                                        className="h-4 w-4 mt-0.5 flex-none text-gray-400"
                                        strokeWidth={2.5}
                                    />
                                    {row.agency}
                                </div>
                                <div className="p-4 md:p-5 flex items-start gap-2.5 text-[13.5px] md:text-[14px] leading-[1.65] text-[#0A0A0A] font-medium bg-white border-l border-black/[0.08]">
                                    <Check
                                        className="h-4 w-4 mt-0.5 flex-none text-[#0A0A0A]"
                                        strokeWidth={2.5}
                                    />
                                    {row.mine}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </FadeUp>

            <FadeUp delay={0.2}>
                <div className="mt-10 md:mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                    <button
                        data-testid="why-different-cta"
                        onClick={() => scrollTo("final-cta")}
                        className="inline-flex items-center justify-center gap-2 rounded-full h-12 px-7 bg-[#0A0A0A] hover:bg-black text-white text-sm font-semibold transition-all hover:-translate-y-[1px]"
                    >
                        Book a Strategy Call
                        <ArrowRight className="h-4 w-4" />
                    </button>
                    <p className="text-[13.5px] md:text-sm text-gray-500 leading-[1.65] max-w-md">
                        30-minute call. We'll map what your first 30 days would look like. No pitch, no pressure.
                    </p>
                </div>
            </FadeUp>
        </div>
    </section>
);

export default WhyDifferent;
